/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, EventEmitter, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { Observable, Subject, debounceTime, switchMap, startWith, map, of, takeUntil } from 'rxjs';
import { ICompanyDetails } from 'src/app/shared/models/i-company.modal';
import { CleanupService } from 'src/app/shared/services/cleanup.service';
import { PrecastService } from 'src/app/shared/services/precast.service';
import { PrecastSampleType } from 'src/app/shared/models/i-precast-sampletypes.modal';
import { nativeMergeColumns } from 'src/app/features/merge/constants/ns-merge-table-columns';
import { precastMergeColumns } from 'src/app/features/merge/constants/pre-merge-table-columns';
import { DatePipe } from '@angular/common';
import { LookupService } from 'src/app/shared/services/lookup.service';
import { CombineTable } from '../../models/combine-indo.model';

@Component({
    selector: 'app-combine',
    templateUrl: './combine.component.html',
    styleUrls: ['./combine.component.scss'],
    providers: [DatePipe],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CombineComponent implements OnInit {
  dataSource:CombineTable[] = [];
  companyFormGroup: FormGroup;
  isCompanyReadonly = false;
  companies: ICompanyDetails[];
  searchCompanyInput = '';
  filteredCompanies: Observable<ICompanyDetails[]>;
  private fetchCompanySubject = new Subject<string>();
  selectedCompanyId: string;
  searchKeyWord: any = '';
  soilType = '';
  ProjectNumber = '';
  companyId = '';
  precastSampleTypes : PrecastSampleType [] = [];
  showSearchResultCount = false;
  headerColumns: object[] = [];
  displayedColumns: string[] = [];
  btnEnabled = true;
  radiobtndisabled = false;
  sampleCount = true;
  constructor(
    private fb: FormBuilder,
    private dashboardService: PrecastService,
    private apiservice: PrecastService,
    private cleanupService: CleanupService,
    private lookupService:LookupService,
  ) {
    this.companyFormGroup = this.fb.group({
      companyId: [''],
      name: ['', Validators.required],
      invoice: [''],
      hasSameBilling: [false],
      materialType: [],
    });
  }

  ngOnInit(): void {
    this.companyFormGroup.get('materialType')?.valueChanges.subscribe((value) => {
      this.soilType = value;
      this.checkSearchButtonState();
    });
    this.checkSearchButtonState();
    this.searchCompanyInput = this.companyFormGroup?.controls['name'].value;
    this.fetchCompanySubject
      .pipe(
        debounceTime(300), // Add a 300ms delay
        switchMap((searchCompanyName: string) => this.dashboardService?.fetchCompanyDetail(searchCompanyName)),
      )
      .subscribe({
        next: (response: ICompanyDetails) => {
          this.companies = Object.assign([], response);
          this.filteredCompanies = this.companyFormGroup.controls['name'].valueChanges.pipe(
            startWith(''),
            map((value) => this.filterStates(value || '')),
          );
        },
      });
    this.getPrecastSampleTypes();
  }

  getControl(controlName: string): FormControl {
    return this.companyFormGroup?.get(controlName) as FormControl;
  }

  copyControl(controlName: string, copyControlName: string): void {
    if (this.getControl(controlName) && this.getControl(copyControlName)) {
      this.getControl(copyControlName).patchValue(this.getControl(controlName).value);
      this.getControl(copyControlName).updateValueAndValidity();
    }
  }

  private filterStates(value: string): ICompanyDetails[] {
    const filterValue = value.toLowerCase();
    // Filter companies based on the input value
    return this.companies.filter((company) => company.name.toLowerCase().includes(filterValue));
  }

  // search

  public onClearSearchButtonClick(): void {
    sessionStorage.removeItem('searchKeyWord');
    this.showSearchResultCount = false;
    this.searchKeyWord = '';
    this.checkSearchButtonState();
  }

  onKeyPress(event: KeyboardEvent): void {
    // Check if searchKeyWord is not empty or undefined
    if (this.searchKeyWord) {
      this.btnEnabled = true;
      // Remove 'searchKeyWord' from session storage
      sessionStorage.removeItem('searchKeyWord');
    }
    this.checkSearchButtonState();
  }

  onSearchChange(): void {
    this.showSearchResultCount = false;
    this.checkSearchButtonState();
    if (!this.searchKeyWord) {
      this.onClearSearchButtonClick();
    }
  }

  checkSearchButtonState(): void {
    // Check if all values are valid
    const isValidSearchKeyWord = this.searchKeyWord.trim() !== '';
    const isValidSoilType = this.soilType === 'nativesoil' || this.soilType === 'precast';
    // Enable or disable the button based on the conditions
    this.btnEnabled = !(isValidSearchKeyWord && isValidSoilType);
  }

  getPrecastSampleTypes() {
    this.lookupService.getPrecastSampletypes().subscribe({
      next: (res: PrecastSampleType[]) => {
        this.precastSampleTypes = res;
      },
    });
  }

  setSoilConfigData(): void {
    this.displayedColumns = [];
    if (this.soilType === 'nativesoil') {
      this.headerColumns = nativeMergeColumns;
      this.headerColumns.forEach((elem: any) => {
        this.displayedColumns.push(elem.name);
      });
    } else if (this.soilType === 'precast') {
      this.headerColumns = precastMergeColumns;
      this.headerColumns.forEach((elem: any) => {
        this.displayedColumns.push(elem.name);
      });
    }
    this.checkSearchButtonState();
  }

  onSearchButtonClick(): void {
    this.dataSource = [];
    this.radiobtndisabled = true;
    this.apiservice.getCombineTableDetail(this.searchKeyWord, this.soilType)
      .pipe(takeUntil(this.cleanupService.unsubscribe))
      .subscribe((data: Array<CombineTable>) => {
        if (this.soilType === 'nativesoil' || this.soilType === 'precast') {
          this.setSoilConfigData();
        }
        if (data) {
          this.btnEnabled = true;
          this.dataSource = data.map((elem: CombineTable) => {
            let el: any;

            if (this.soilType === 'nativesoil') {
              el = this.setSampleDataForDisplay(elem);
            } else {
              el = this.setSampleDataForPrecast(elem);
              el = {
                ...elem,
              };
            }

            // Ensure that nativeSoilSamples is handled even if empty
            if (Array.isArray(el.nativeSoilSamples) && el.nativeSoilSamples.length === 0) {
              el.nativeSoilSamples = []; // If empty, set as an empty array
            }
            return el;
          });
        }
      });
  }

  setSampleDataForDisplay(elem: any): any {
    // Clone the element to avoid mutating the original data directly
    const el: any = {
      ...elem,
    };
    // Iterate through each native soil sample and assign displayDepth
    if (el.nativeSoilSamples && el.nativeSoilSamples.length > 0) {
      el.nativeSoilSamples = el.nativeSoilSamples.map((sample: any) => {
        // Handle moisture content logic for each sample
        if (sample.moistureOptimum) {
          sample.moistureContent = 'Optimum';
        } else if (sample.moistureAsReceived) {
          sample.moistureContent = 'In-Situ/As Received';
        } else {
          sample.moistureContent = sample.specifiedMoistureContent;
        }
        // Handle sampleDepth and sampleEndDepth logic
        if (sample.sampleDepth === '') {
          sample.sampleDepth = null;
        }
        // Construct displayDepth based on sampleDepth and sampleEndDepth
        if (sample.sampleEndDepth !== '') {
          sample.displayDepth = `${sample.sampleDepth}-${sample.sampleEndDepth}`;
        }
        // If sampleDepth is undefined and sampleEndDepth exists, mark as "Stockpile"
        if (sample.sampleDepth === undefined && sample.sampleEndDepth !== '') {
          sample.displayDepth = 'Stockpile';
        }

        sample.sampleTypeMasterId = sample.sampleTypeMasterId === '3ab1b1ca-7964-4a72-9818-202f0b3c592b' ? 'Bulk' : 'Tube';
        return sample;
      });
    }

    return el;
  }

  setSampleDataForPrecast(elem: any): any {
    const el: any = {
      ...elem,
    };
      // Iterate through each precast sample and assign the label based on the sampleTypeMasterId
    if (el.preCastSamples && el.preCastSamples.length > 0) {
      el.preCastSamples = el.preCastSamples.map((sample: any) => {
        // Find the corresponding sampleType from the precastSampleTypes array
        const matchedSampleType = this.precastSampleTypes.find((type: PrecastSampleType) => type.id === sample.sampleTypeMasterId);
        // If a match is found, assign the label
        if (matchedSampleType) {
          sample.sampleTypeMasterId = matchedSampleType.label;
        } else {
          // If no match is found, you can assign a default label or leave it empty
          sample.sampleTypeMasterId = 'Unknown Sample Type'; // Or any other default label
        }

        return sample;
      });
    }

    return el;
  }

  handleSampleCountExceeded(isExceeded: boolean): void {
    if (isExceeded) {
      this.sampleCount = false;
      // Handle the condition when sample count exceeds 50
    } else {
      this.sampleCount = true;
      // Handle the case when sample count is within limit
    }
  }
}
